/**
 * Turning an API response into a file on somebody's computer.
 *
 * PROVIA's backend is an Apps Script web app, which can only answer with a
 * JSON envelope — there is no way for it to set Content-Disposition and let
 * the browser do this the ordinary way (see 09_Router.gs for why every call is
 * a POST of text/plain). So a download arrives as data and the page assembles
 * the file itself. The upside is that it works identically for a CSV and a
 * PDF; the cost is this function.
 */
export interface ExportedFile {
  filename: string;
  mime: string;
  encoding: 'text' | 'base64';
  content: string;
}

/** Base64 to bytes, without pulling in a library to do it. */
function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function saveFile(file: ExportedFile) {
  const body = file.encoding === 'base64'
    ? decodeBase64(file.content)
    : file.content;

  const blob = new Blob([body as BlobPart], { type: file.mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.filename;
  /*
   * Appended before the click and removed after. Firefox ignores a click on an
   * anchor that is not in the document, which is the sort of thing that works
   * in testing on one machine and fails for a quarter of real users.
   */
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
